"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiGetQuiet, apiPost, apiPostQuiet } from "@/lib/api";

export interface AuthUserInfo {
  username: string;
  email: string;
  role: string;
  mustChangePassword: boolean;
}

interface AuthContextValue {
  user: AuthUserInfo | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<AuthUserInfo>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setUser: (u: AuthUserInfo | null) => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => ({ username: "", email: "", role: "user", mustChangePassword: false }),
  logout: async () => {},
  refresh: async () => {},
  setUser: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

function normalize(res: {
  username: string;
  email?: string;
  role?: string;
  mustChangePassword?: boolean;
  must_change_password?: boolean;
}): AuthUserInfo {
  return {
    username: res.username,
    email: res.email ?? "",
    role: res.role ?? "user",
    mustChangePassword: res.mustChangePassword ?? res.must_change_password ?? false,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await apiGetQuiet<{
        username: string;
        email: string;
        role: string;
        mustChangePassword?: boolean;
        must_change_password?: boolean;
      }>("/auth/me");
      setUser(normalize(res));
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await apiPostQuiet<{
      username: string;
      email: string;
      role: string;
      mustChangePassword?: boolean;
      must_change_password?: boolean;
    }>("/auth/login", { username, password });
    const u = normalize(res);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await apiPost("/auth/logout", {});
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
